import { useEffect, useRef } from 'react';

import Icon from 'components/Icon';
import { ESQUEMA_LOJA, urlDaLoja } from 'config/appAndroid';
import useVersaoApp from 'hooks/useVersaoApp';
import { abrirEmAppNativo } from 'services/navegacaoExterna';
import { BLOQUEADA } from 'services/versaoApp';

/**
 * A atualização obrigatória do app (hooks/useVersaoApp): abaixo da versão publicada, e com ela já
 * oferecida pela Play Store deste aparelho, uma tela cheia por cima de tudo, cuja única saída é
 * atualizar. Não há "Agora não": toda atualização é necessária. Antes de a loja oferecer, nada aparece,
 * para ninguém ficar preso diante de uma loja sem nada para instalar.
 *
 * O texto segue o padrão dos apps grandes (o iFood, em 2025: "Atualize pra usar o app"): o que fazer, uma
 * razão curta, um botão só e a versão instalada para quem precisar falar com o suporte.
 *
 * "Atualizar na Play Store" abre a página do app no app da Play Store; sem ele, a página da loja no
 * navegador. A instalação reinicia o app, e a checagem da abertura seguinte já não encontra nada.
 *
 * z-[1150]: acima do ConsentimentoApp (1100), que não deve esconder o bloqueio, e abaixo da abertura
 * (SplashApp, 1200), que sai sozinha em dois segundos. Na web devolve null.
 */
const abrirLoja = () => abrirEmAppNativo({ android: ESQUEMA_LOJA, web: urlDaLoja('atualizacao') });

const AvisoVersaoApp = () => {
	const { situacao, versaoInstalada } = useVersaoApp();
	const botaoRef = useRef(null);
	const bloqueada = situacao === BLOQUEADA;

	useEffect(() => {
		if (bloqueada) botaoRef.current?.focus();
	}, [bloqueada]);

	if (!bloqueada) return null;

	return (
		<div
			role="alertdialog"
			aria-modal="true"
			aria-labelledby="versao-bloqueio-titulo"
			aria-describedby="versao-bloqueio-texto"
			className="fixed inset-0 z-[1150] flex flex-col bg-surface px-6 pt-[calc(3.5rem+var(--safe-top))] pb-[calc(2rem+var(--safe-bottom))]"
		>
			<div className="mx-auto flex w-full max-w-[560px] flex-1 flex-col items-center justify-center gap-5 text-center">
				<span className="flex h-[88px] w-[88px] items-center justify-center rounded-full bg-primary/10">
					<Icon name="mobile_arrow_down" className="h-11 w-11 text-primary" />
				</span>
				<div className="flex max-w-[320px] flex-col gap-3">
					<h1 id="versao-bloqueio-titulo" className="text-2xl font-bold leading-tight text-dark">
						Atualize o app para continuar
					</h1>
					<p id="versao-bloqueio-texto" className="t-corpo">
						Há uma versão nova do Nova Faxina na Play Store. Atualize para continuar usando o app.
					</p>
					<p className="t-apoio">Não se preocupe: seus dados continuam salvos.</p>
				</div>
				{/* O selo oficial, o mesmo PNG do components/SeloGooglePlay, sem edição (as diretrizes do
				    Google proíbem recolorir ou recortar). Só a imagem, e não o link do SeloGooglePlay: a ação
				    é o botão de baixo, e um segundo alvo para a mesma coisa tiraria o "um botão só" da tela.
				    Hoje o app só existe na Play; com a App Store, este selo passa a depender da plataforma. */}
				<img
					src="/images/badges/google-play-pt-br.png"
					width="646"
					height="250"
					alt="Disponível no Google Play"
					className="h-[52px] w-auto"
				/>
			</div>
			<div className="mx-auto flex w-full max-w-[560px] flex-col gap-3.5">
				<button
					ref={botaoRef}
					type="button"
					onClick={abrirLoja}
					className="btn btn-primary min-h-12 w-full justify-center"
				>
					Atualizar na Play Store
				</button>
				{versaoInstalada && (
					<p className="text-center text-xs text-muted">Versão instalada: {versaoInstalada}</p>
				)}
				{/* A linha legal que o selo pede. Na web ela fica no rodapé; aqui não há rodapé. */}
				<p className="text-center text-2xs text-muted">
					Google Play e o logotipo do Google Play são marcas da Google LLC.
				</p>
			</div>
		</div>
	);
};

export default AvisoVersaoApp;
